import { useEffect, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { previewGestionSync, confirmGestionSync, getSyncJob,
         markSyncResultManual, unmarkSyncResultManual } from "../lib/api";
import { formatEUR } from "../lib/format";
import { Loader2, CheckCircle2, AlertCircle, ExternalLink, UserCheck, Undo2 } from "lucide-react";
import { toast } from "sonner";

export default function GestionSyncDialog({ open, onOpenChange, invoiceId, invoiceNumber, invoiceType = "advance", trips }) {
  const [phase, setPhase] = useState("idle"); // idle | preview | running | done
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({}); // id -> bool
  const [results, setResults] = useState([]);
  const [job, setJob] = useState(null); // {id, status, total, done, log[], results[]}
  const pollRef = useRef(null);

  const [pendingCount, setPendingCount] = useState(0);
  const [syncedCount, setSyncedCount] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const isFinal = invoiceType === "final";
  const amountKey = isFinal ? "final_amount" : "advance_amount";
  const amountLabel = isFinal ? "Pago final" : "Adelanto";
  const targetFieldLabel = isFinal ? "Fact. Final + Notas" : "Fact. Adelanto";

  // Cleanup polling on close/unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const reset = () => {
    setPhase("idle"); setItems([]); setSelected({}); setResults([]); setJob(null);
    setPendingCount(0); setSyncedCount(0); setLastSyncAt(null);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };

  const handlePreview = async () => {
    setPhase("running");
    try {
      const res = await previewGestionSync(invoiceId);
      const list = res.items || [];
      setItems(list);
      // Default selection: only the NOT-yet-synced trips are pre-checked
      const sel = {};
      list.forEach((it) => { sel[it.trip_id_local] = !it.already_synced; });
      setSelected(sel);
      setSyncedCount(res.already_synced_count || 0);
      setPendingCount(list.length - (res.already_synced_count || 0));
      setLastSyncAt(res.last_sync_at || null);
      setPhase("preview");
    } catch (err) {
      toast.error("Error preparando preview: " + (err?.response?.data?.detail || err.message));
      setPhase("idle");
    }
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const j = await getSyncJob(jobId);
        setJob(j);
        if (j.status === "done" || j.status === "error" || j.status === "cancelled") {
          clearInterval(pollRef.current); pollRef.current = null;
          setResults(j.results || []);
          setPhase("done");
          if (j.status === "done") {
            const ok = (j.results || []).filter((r) => r.status === "ok").length;
            toast.success(`${ok}/${j.results.length} viajes sincronizados`);
          } else {
            toast.error(`Sincronización ${j.status}: ${j.error || ""}`);
          }
        }
      } catch (err) {
        // job might have expired (server restart); stop polling
        clearInterval(pollRef.current); pollRef.current = null;
        toast.error("Conexión con el job perdida: " + (err?.response?.data?.detail || err.message));
        setPhase("done");
      }
    }, 2500);
  };

  const handleConfirm = async () => {
    const tripIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!tripIds.length) { toast.error("Selecciona al menos un viaje"); return; }
    if (!confirm(`Vas a actualizar ${tripIds.length} viajes en el gestor. ¿Continuar?`)) return;
    try {
      const res = await confirmGestionSync(invoiceId, { tripIds });
      // res = {job_id, status, total, type}
      setJob({ ...res, id: res.job_id, done: 0, log: [], results: [] });
      setPhase("running");
      startPolling(res.job_id);
    } catch (err) {
      toast.error("Error iniciando sincronización: " + (err?.response?.data?.detail || err.message));
    }
  };

  const allSelected = items.length > 0 && items.every((it) => selected[it.trip_id_local]);
  const togglePendingOnly = () => {
    // Select only the not-yet-synced (default smart selection)
    const sel = {};
    items.forEach((it) => { sel[it.trip_id_local] = !it.already_synced; });
    setSelected(sel);
  };
  const toggleAll = () => {
    const next = !allSelected;
    const sel = {};
    items.forEach((it) => { sel[it.trip_id_local] = next; });
    setSelected(sel);
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totalAmount = items
    .filter((it) => selected[it.trip_id_local])
    .reduce((s, it) => s + (it[amountKey] || 0), 0);

  const okCount = results.filter((r) => r.status === "ok" || r.status === "manual_done").length;

  // Update one result in-place when user toggles its 'manual_done' state
  const updateResult = (tripIdLocal, patch) => {
    setResults((prev) => prev.map((r) =>
      r.trip_id_local === tripIdLocal ? { ...r, ...patch } : r
    ));
  };

  const handleMarkManual = async (r) => {
    if (!r.trip_id_local) return;
    const note = window.prompt(
      "¿Algún comentario sobre cómo lo hiciste manual? (opcional)",
      "",
    );
    if (note === null) return; // user cancelled
    try {
      const res = await markSyncResultManual(invoiceId, r.trip_id_local, note || null);
      updateResult(r.trip_id_local, res.result);
      toast.success(`${r.lead_name}: marcado como hecho manualmente`);
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };

  const handleUnmarkManual = async (r) => {
    if (!r.trip_id_local) return;
    try {
      const res = await unmarkSyncResultManual(invoiceId, r.trip_id_local);
      updateResult(r.trip_id_local, res.result);
      toast.success(`${r.lead_name}: marca manual eliminada`);
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="gestion-sync-dialog">
        <DialogHeader>
          <DialogTitle className="font-cabinet text-2xl tracking-tighter">
            Sincronizar con Gestor de Trips
          </DialogTitle>
          <DialogDescription>
            {isFinal ? (
              <>Escribe automáticamente <strong>Fact. Final</strong> = {invoiceNumber} y añade una nota
              con el importe del pago final por trip en <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">gestion.viajadverdad.com</code>.</>
            ) : (
              <>Escribe automáticamente <strong>Adelanto</strong> y <strong>Fact. Adelanto</strong> = {invoiceNumber} en cada
              viaje correspondiente de <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">gestion.viajadverdad.com</code>.</>
            )} El emparejamiento se hace por <strong>Lead Name</strong>.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="py-10 space-y-4 text-center">
            <p className="text-sm text-slate-600">
              Esta factura tiene <strong>{trips?.length || 0}</strong> viajes con adelanto.
              Pulsa &quot;Preparar&quot; para previsualizar qué se va a actualizar.
            </p>
          </div>
        )}

        {phase === "running" && (
          <div className="py-10 px-4 flex flex-col items-center gap-4">
            {!job || job.status === "queued" || (job.status === "running" && job.total === 0) ? (
              <>
                <Loader2 size={28} className="animate-spin text-slate-500" />
                <div className="text-sm text-slate-500">
                  {items.length === 0 ? "Cargando preview..." : "Iniciando navegador..."}
                </div>
              </>
            ) : (
              <>
                <div className="w-full max-w-md">
                  <div className="flex justify-between text-xs text-slate-600 mb-2">
                    <span>Sincronizando viajes...</span>
                    <span className="font-mono tabular-nums">{job.done}/{job.total}</span>
                  </div>
                  <Progress value={(job.done / Math.max(1, job.total)) * 100} className="h-2" data-testid="sync-progress" />
                </div>
                <div className="text-xs text-slate-400 font-mono max-h-32 overflow-y-auto w-full max-w-md bg-slate-50 border border-slate-100 rounded p-2">
                  {(job.log || []).slice(-6).map((l, i) => <div key={`log-${i}-${l.slice(0, 20)}`}>{l}</div>)}
                </div>
                <div className="text-[10px] text-slate-400">
                  Puedes cerrar esta ventana — el sync sigue en segundo plano. Volviendo a abrir el diálogo verás el resultado.
                </div>
              </>
            )}
          </div>
        )}

        {phase === "preview" && (
          <div className="flex-1 overflow-auto -mx-6 px-6 py-2">
            {/* Status banner */}
            <div className="mb-3 grid grid-cols-3 gap-2 text-xs">
              <div className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-900">
                <div className="text-[10px] uppercase tracking-wider font-bold">Ya sincronizados</div>
                <div className="text-base font-bold tabular-nums">{syncedCount}</div>
              </div>
              <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900">
                <div className="text-[10px] uppercase tracking-wider font-bold">Pendientes</div>
                <div className="text-base font-bold tabular-nums">{pendingCount}</div>
              </div>
              <div className="px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-blue-900">
                <div className="text-[10px] uppercase tracking-wider font-bold">Seleccionados ahora</div>
                <div className="text-base font-bold tabular-nums">{selectedCount} · {formatEUR(totalAmount)}</div>
              </div>
            </div>
            {lastSyncAt && (
              <div className="mb-2 text-[10px] text-slate-500">
                Última sincronización: {new Date(lastSyncAt).toLocaleString("es-ES")}
              </div>
            )}
            <div className="mb-2 flex gap-2 text-[11px]">
              <button onClick={togglePendingOnly} className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid="select-pending-btn">
                Seleccionar solo pendientes
              </button>
              <button onClick={toggleAll} className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50">
                {allSelected ? "Deseleccionar todos" : "Seleccionar todos"}
              </button>
            </div>
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="w-8"></TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Lead Name</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-right">{amountLabel}</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">{targetFieldLabel}</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase text-center">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.trip_id_local} className={`border-slate-100 ${it.already_synced ? "bg-emerald-50/30" : ""}`}>
                    <TableCell>
                      <Checkbox
                        checked={!!selected[it.trip_id_local]}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [it.trip_id_local]: !!v }))}
                        data-testid={`sync-check-${it.trip_id_local}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{it.lead_name}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{formatEUR(it[amountKey])}</TableCell>
                    <TableCell className="text-slate-600 font-mono">{it.invoice_number}</TableCell>
                    <TableCell className="text-center">
                      {it.already_synced ? (
                        <span className="text-emerald-700 text-[11px] font-semibold">✓ Sincronizado</span>
                      ) : (
                        <span className="text-amber-700 text-[11px] font-semibold">⏳ Pendiente</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {phase === "done" && (
          <div className="flex-1 overflow-auto -mx-6 px-6 py-2">
            <div className={`mb-3 px-4 py-3 rounded-md text-sm flex items-center gap-2 ${
              okCount === results.length ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-amber-50 text-amber-900 border border-amber-200"
            }`}>
              {okCount === results.length ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
              <span><strong>{okCount}/{results.length}</strong> viajes sincronizados correctamente</span>
            </div>
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-[10px] font-semibold uppercase">Lead Name</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Trip ID</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Antes</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Después</TableHead>
                  <TableHead className="text-[10px] font-semibold uppercase">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r, i) => (
                  <TableRow key={r.trip_id_local || `${r.lead_name}-${i}`} className="border-slate-100">
                    <TableCell className="font-medium text-slate-900">{r.lead_name}</TableCell>
                    <TableCell className="font-mono text-slate-600">
                      {r.trip_id ? (
                        <a
                          href={`https://gestion.viajadverdad.com/trips/form/1/${r.trip_id}`}
                          target="_blank" rel="noreferrer"
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                        >
                          {r.trip_id} <ExternalLink size={10} />
                        </a>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-slate-500 max-w-[180px] truncate">
                      {r.before ? (isFinal
                        ? `${r.before.fact_final || "—"} / ${(r.before.notas || "—").slice(0, 30)}`
                        : `${r.before.adelanto || "—"} / ${r.before.fact_adelanto || "—"}`
                      ) : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-slate-700 max-w-[180px] truncate">
                      {r.after ? (isFinal
                        ? `${r.after.fact_final} / ${(r.after.notas || "").slice(0, 30)}`
                        : `${r.after.adelanto} / ${r.after.fact_adelanto}`
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {r.status === "ok" && <span className="text-emerald-700">✓ OK</span>}
                        {r.status === "not_found" && <span className="text-amber-700">⚠ No encontrado</span>}
                        {r.status === "error" && <span className="text-rose-700">✗ Error</span>}
                        {r.status === "save_unverified" && <span className="text-amber-700">⚠ Guardado sin verificar</span>}
                        {r.status === "manual_done" && (
                          <span className="text-slate-700 inline-flex items-center gap-1" title={
                            (r.manual_done_at ? `Marcado el ${r.manual_done_at.slice(0, 16).replace("T", " ")}` : "") +
                            (r.manual_done_by ? `\npor ${r.manual_done_by}` : "") +
                            (r.manual_note ? `\nNota: ${r.manual_note}` : "")
                          }>
                            <UserCheck size={12} /> Hecho manualmente
                          </span>
                        )}
                        {/* Action button: mark as manual (only when not OK and not already manual) */}
                        {r.trip_id_local && r.status !== "ok" && r.status !== "manual_done" && (
                          <button
                            type="button"
                            onClick={() => handleMarkManual(r)}
                            className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-900 text-white hover:bg-slate-800"
                            data-testid={`mark-manual-${r.trip_id_local}`}
                            title="Lo he hecho manualmente en el CRM"
                          >
                            <UserCheck size={10} /> Marcar manual
                          </button>
                        )}
                        {r.status === "manual_done" && r.trip_id_local && (
                          <button
                            type="button"
                            onClick={() => handleUnmarkManual(r)}
                            className="ml-auto p-1 rounded text-slate-500 hover:bg-slate-100"
                            data-testid={`unmark-manual-${r.trip_id_local}`}
                            title="Quitar marca manual"
                          >
                            <Undo2 size={11} />
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter>
          {phase === "idle" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sync-cancel-btn">Cancelar</Button>
              <Button onClick={handlePreview} className="bg-slate-900 hover:bg-slate-800" data-testid="sync-preview-btn">
                Preparar preview
              </Button>
            </>
          )}
          {phase === "preview" && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sync-cancel2-btn">Cancelar</Button>
              <Button onClick={handleConfirm} disabled={selectedCount === 0} className="bg-slate-900 hover:bg-slate-800" data-testid="sync-confirm-btn">
                Sincronizar {selectedCount} viajes
              </Button>
            </>
          )}
          {phase === "done" && (
            <Button onClick={() => onOpenChange(false)} className="bg-slate-900 hover:bg-slate-800" data-testid="sync-close-btn">
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
